import { createContext, ReactNode, useContext, useMemo } from "react";
import type { CheckoutSession } from "../../domain/entities/CheckoutSession";
import { GetOrderByIdUseCase } from "../../application/usecases/GetOrderByIdUseCase";
import { GetPaymentMethodsByCountryUseCase } from "../../application/usecases/GetPaymentMethodsByCountryUseCase";
import { ProcessPaymentUseCase } from "../../application/usecases/ProcessPaymentUseCase";
import { UploadPaymentVoucherUseCase } from "../../application/usecases/UploadPaymentVoucherUseCase";
import { GetLatestTransactionByReferenceCodeUseCase } from "../../application/usecases/GetLatestTransactionByReferenceCodeUseCase";
import { GetExchangeRateUseCase } from "../../application/usecases/GetExchangeRateUseCase";
import { OrdersHttpAdapter } from "../../infrastructure/adapters/http/OrdersHttpAdapter";
import { PaymentsHttpAdapter } from "../../infrastructure/adapters/http/PaymentsHttpAdapter";
import { PaymentsProcessorHttpAdapter } from "../../infrastructure/adapters/http/PaymentsProcessorHttpAdapter";
import { PaymentTransactionsHttpAdapter } from "../../infrastructure/adapters/http/PaymentTransactionsHttpAdapter";
import { ExchangeRateHttpAdapter } from "../../infrastructure/adapters/http/ExchangeRateHttpAdapter";

interface ApplicationContextValue {
  session: CheckoutSession;
  getOrderById: GetOrderByIdUseCase;
  getPaymentMethodsByCountry: GetPaymentMethodsByCountryUseCase;
  processPayment: ProcessPaymentUseCase;
  uploadPaymentVoucher: UploadPaymentVoucherUseCase;
  getLatestTransactionByReferenceCode: GetLatestTransactionByReferenceCodeUseCase;
  getExchangeRate: GetExchangeRateUseCase;
}

const ApplicationContext = createContext<ApplicationContextValue | undefined>(undefined);

interface ApplicationProviderProps {
  children: ReactNode;
  session: CheckoutSession;
  useMocks?: boolean;
}

const DEFAULT_ORDERS_BASE_URL = process.env.REACT_APP_ORDERS_API_BASE_URL ?? "https://prod.api.actual.petsrwise.com";
const DEFAULT_PAYMENTS_BASE_URL =
  process.env.REACT_APP_PAYMENTS_API_BASE_URL ?? "https://prod.api.actual.petsrwise.com/api/payments";
const DEFAULT_CURRENCY = process.env.REACT_APP_DEFAULT_CURRENCY ?? "USD";
const DEFAULT_EXCHANGE_RATE_URL =
  process.env.REACT_APP_EXCHANGE_RATE_URL ?? "https://api.dolarvzla.com/public/exchange-rate";

export const ApplicationProvider = ({ children, session, useMocks = false }: ApplicationProviderProps) => {
  const services = useMemo<ApplicationContextValue>(() => {
    const orderQueryPort = new OrdersHttpAdapter({
      baseUrl: DEFAULT_ORDERS_BASE_URL,
      authorizationToken: session.authorizationToken,
      currency: DEFAULT_CURRENCY,
    });

    const paymentMethodQueryPort = new PaymentsHttpAdapter({
      baseUrl: DEFAULT_PAYMENTS_BASE_URL,
      authorizationToken: session.authorizationToken,
    });

    const paymentsProcessorAdapter = new PaymentsProcessorHttpAdapter({
      baseUrl: DEFAULT_PAYMENTS_BASE_URL,
      authorizationToken: session.authorizationToken,
    });

    const paymentTransactionsAdapter = new PaymentTransactionsHttpAdapter({
      baseUrl: DEFAULT_PAYMENTS_BASE_URL,
      authorizationToken: session.authorizationToken,
    });

    const exchangeRateAdapter = new ExchangeRateHttpAdapter({
      baseUrl: DEFAULT_EXCHANGE_RATE_URL,
    });

    return {
      session,
      getOrderById: new GetOrderByIdUseCase(orderQueryPort),
      getPaymentMethodsByCountry: new GetPaymentMethodsByCountryUseCase(paymentMethodQueryPort),
      processPayment: new ProcessPaymentUseCase(paymentsProcessorAdapter),
      uploadPaymentVoucher: new UploadPaymentVoucherUseCase(paymentsProcessorAdapter),
      getLatestTransactionByReferenceCode: new GetLatestTransactionByReferenceCodeUseCase(
        paymentTransactionsAdapter,
      ),
      getExchangeRate: new GetExchangeRateUseCase(exchangeRateAdapter),
    };
  }, [session, useMocks]);

  return <ApplicationContext.Provider value={services}>{children}</ApplicationContext.Provider>;
};

export const useApplication = (): ApplicationContextValue => {
  const context = useContext(ApplicationContext);

  if (!context) {
    throw new Error("useApplication must be used within an ApplicationProvider");
  }

  return context;
};
